import { Integration } from '@sentry/core';
import { ExclusiveEventHintOrCaptureContext } from '@sentry/core/build/types/utils/prepareEvent';
import type { BrowserOptions, ErrorEvent, EventHint } from '@sentry/react';
import * as Sentry from '@sentry/react';
import { History } from 'history';

/**
 * Router arguments for React Router v4-5 integration
 */
export interface RouterArgs {
  history: History;
  // Optional routes config if using centralized routes
  routes?: any[];
}

/**
 * Configuration options for Sentry initialization
 * Extends BrowserOptions which is the correct type for browser-based Sentry
 */
export interface SentryInitOptions {
  /** Sentry DSN */
  dsn: string;
  /** React Router arguments */
  routerArgs?: RouterArgs;
  /** Enable performance monitoring */
  enablePerformanceMonitoring?: boolean;
  /** Enable session replay */
  enableSessionReplay?: boolean;
  /** Environment (e.g., 'dev', 'test', 'production') */
  environment: string;
  /** Release version */
  release?: string;
  /** Trace sample rate */
  tracesSampleRate?: number;
  /** Session replay sample rate */
  replaysSessionSampleRate?: number;
  /** Error replay sample rate */
  replaysOnErrorSampleRate?: number;
  /** Attach stack traces to events */
  attachStacktrace?: boolean;
  /** Maximum number of breadcrumbs */
  maxBreadcrumbs?: number;
  /** Send default PII data */
  sendDefaultPii?: boolean;
  /** Custom beforeSend function */
  beforeSend?: (event: ErrorEvent, hint: EventHint) => ErrorEvent | null | undefined;
}

/**
 * Initialize Sentry for a React application with comprehensive configuration
 * @param config - Configuration options
 */
export const initializeSentry = ({
  dsn,
  routerArgs,
  enablePerformanceMonitoring = true,
  enableSessionReplay = true,
  environment,
  release,
  tracesSampleRate = 1.0,
  replaysSessionSampleRate = 0,
  replaysOnErrorSampleRate = 1.0,
  attachStacktrace = true,
  maxBreadcrumbs = 100,
  sendDefaultPii = false,
  beforeSend,
}: SentryInitOptions): void => {
  // Only initialize Sentry in test and production environments
  if (environment !== 'test' && environment !== 'production') {
    console.log('[Sentry] Initialization skipped - only enabled in test and production environments');
    return;
  }

  // Set up integrations based on enabled features
  const integrations: Integration[] = [];

  // Add React Router integration if routerArgs provided
  // Using React Router v4-5 integration
  if (routerArgs) {
    integrations.push(Sentry.reactRouterV5BrowserTracingIntegration(routerArgs));
  }

  // Add browser tracing if performance monitoring enabled
  if (enablePerformanceMonitoring) {
    integrations.push(Sentry.browserTracingIntegration());
    integrations.push(Sentry.browserProfilingIntegration());
  }

  // Add replay integration if session replay enabled
  if (enableSessionReplay && environment === 'production') {
    integrations.push(Sentry.replayIntegration());
  }

  // Only enable trace propagation for same-origin requests
  // This prevents CORS issues with the sentry-trace header in cross-origin requests
  const tracePropagationTargets: Array<RegExp | string> = [
    // Only include same-origin requests
    /^\//, // Same-origin paths
  ];

  integrations.push(
    Sentry.feedbackIntegration({
      colorScheme: 'system',
      enableScreenshot: true,
      isNameRequired: false,
      isEmailRequired: false,
      showBranding: false,
      useSentryUser: {
        name: 'username',
        email: 'email',
      },
    }),
  );

  // Initialize Sentry with comprehensive configuration
  // Use type assertion to handle replay properties that might not be in the type definition
  Sentry.init({
    dsn,
    integrations,
    environment,
    release,
    tracesSampleRate,
    tracePropagationTargets,
    attachStacktrace,
    maxBreadcrumbs,
    sendDefaultPii,
    beforeSend,
    // Disable automatic instrumentation of fetch/XHR to prevent CORS issues
    instrumenter: {
      disabled: ['fetch', 'xhr'],
    },
    // Handle replay properties with a type assertion
    // These properties are available in the latest Sentry SDK but TypeScript definitions
    // might not be up-to-date yet
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,
    profilesSampleRate: 1.0,
  } as BrowserOptions);
};

/**
 * Wrapper for Sentry's addBreadcrumb
 * - Only sends to Sentry in test and production environments
 * - In dev environment, uses console.log for local testing
 * - In other environments, does nothing
 * @param breadcrumb - Breadcrumb to add
 */
export const addBreadcrumb = (breadcrumb: Sentry.Breadcrumb): void => {
  // In dev environment, use console.log for local testing
  if (process.env.NODE_ENV === 'dev') {
    console.log(`[Breadcrumb - ${breadcrumb.category || 'default'}] ${breadcrumb.message || ''}`, {
      level: breadcrumb.level,
      type: breadcrumb.type,
      data: breadcrumb.data,
      timestamp: breadcrumb.timestamp || Date.now(),
    });
    return;
  }

  // Only send to Sentry in test and production environments
  if (process.env.NODE_ENV === 'test' || process.env.NODE_ENV === 'production') {
    Sentry.addBreadcrumb(breadcrumb);
  }
};

/**
 * Props for captureMessage function
 */
interface CaptureMessageProps {
  /** Message to capture */
  message: string;
  /** Optional JSON data to include as 'extra' context */
  data?: Record<string, any>;
  /** Optional severity level, defaults to 'info' */
  level?: Sentry.SeverityLevel;
}

/**
 * Wrapper for Sentry's captureMessage
 * - Only sends to Sentry in test and production environments
 * - In dev environment, uses console.log for local testing
 * - Supports additional JSON data via the data parameter
 * - Can be called with just a message string or with a props object
 * @param messageOrProps - Either a message string or an object containing message, optional data and optional level
 * @returns The event ID or empty string when disabled
 */
export const captureMessage = (messageOrProps: string | CaptureMessageProps): string => {
  // Default props
  let message: string;
  let data: Record<string, any> | undefined;
  let level: Sentry.SeverityLevel = 'info';

  // Handle different call patterns
  if (typeof messageOrProps === 'string') {
    // Simple string message
    message = messageOrProps;
  } else {
    // Object with properties
    message = messageOrProps.message;
    data = messageOrProps.data;
    level = messageOrProps.level ?? 'info';
  }

  // In dev environment, use console.log for local testing
  if (process.env.NODE_ENV === 'dev') {
    if (data) {
      console.log(`[${level}] ${message}`, data);
    } else {
      console.log(`[${level}] ${message}`);
    }
    return '';
  }

  // Only send to Sentry in test and production environments
  if (process.env.NODE_ENV === 'test' || process.env.NODE_ENV === 'production') {
    if (data) {
      // Include the data in the 'extra' parameter
      return Sentry.captureMessage(message, {
        level,
        extra: data,
      });
    } else {
      // Original behavior when no data is provided
      return Sentry.captureMessage(message, level);
    }
  }

  return '';
};

/**
 * Wrapper for Sentry's setUser
 * - Only works in test and production environments
 * @param user - User data to set
 */
export const setUser = (user: Sentry.User | null): void => {
  // Only works in test and production environments
  if (process.env.NODE_ENV === 'test' || process.env.NODE_ENV === 'production') {
    Sentry.setUser(user);
  }
};

/**
 * Wrapper for Sentry's captureException
 * - Only sends to Sentry in test and production environments
 * - In local environment, uses console.error for local testing
 * @param exception - Exception to capture
 * @param captureContext - Additional context for the exception
 * @returns The generated event ID or empty string when disabled
 */
export const captureException = (exception: any, captureContext?: ExclusiveEventHintOrCaptureContext): string => {
  // In local environment, use console.error for local testing
  if (process.env.NODE_ENV === 'local') {
    console.error('Exception:', exception);

    // Log additional context if provided
    if (captureContext) {
      console.error('Additional context:', captureContext);
    }

    return '';
  }

  // Only send to Sentry in test and production environments
  if (process.env.NODE_ENV === 'test' || process.env.NODE_ENV === 'production') {
    return Sentry.captureException(exception, captureContext);
  }

  return '';
};

export const setDataAttributeDisplay = (toggle: boolean) => {
  document.documentElement.setAttribute('data-display-sentry-feedback', toggle.toString());
};
